import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { getOtherSubdomains } from "@/subdomains/configs";
import { useSubdomainPaths } from "@/subdomains/useContactPath";

export function SubdomainHeader({ config, Icon, theme }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const paths = useSubdomainPaths(config.slug);
  const navItems = [
    { label: "Home", to: paths.home },
    { label: "Solutions", to: paths.solutions },
    { label: "Modules", to: paths.modules },
    { label: "Contact", to: paths.contact },
  ];

  return (
    <header
      data-testid="subdomain-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-100"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link to={paths.home} className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${theme.primary} flex items-center justify-center`}>
            <Icon size={20} weight="fill" className="text-white" />
          </div>
          <span className="text-lg font-bold text-zinc-900 tracking-tight">{config.name}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              data-testid={`subdomain-nav-${item.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${
                item.to === paths.contact
                  ? pathname === paths.contact || pathname.endsWith("/contact")
                    ? theme.primaryText
                    : "text-zinc-600 hover:text-zinc-900"
                  : item.label === "Home" && (pathname === paths.home || pathname === `${paths.home}/`)
                    ? theme.primaryText
                    : "text-zinc-600 hover:text-zinc-900"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to={paths.contact}
            data-testid="subdomain-header-book-consultation"
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-zinc-800 border border-zinc-200 hover:border-zinc-400 transition-colors"
          >
            Book Consultation
          </Link>
          <Link
            to={paths.contact}
            data-testid="subdomain-header-request-demo"
            className={`hidden sm:inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white ${theme.primary} ${theme.primaryHover} transition-colors`}
          >
            Request Demo
          </Link>
          <button
            type="button"
            className="lg:hidden p-2 text-zinc-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-zinc-100 bg-white px-6 py-4 space-y-3">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className="block text-sm font-medium text-zinc-700 py-2"
            >
              {item.label}
            </Link>
          ))}
          <Link
            to={paths.contact}
            onClick={() => setMobileOpen(false)}
            className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white ${theme.primary}`}
          >
            Request Demo
          </Link>
          <Link
            to={paths.contact}
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-zinc-800 border border-zinc-200"
          >
            Book Consultation
          </Link>
        </div>
      )}
    </header>
  );
}

export function SubdomainFooter({ config, theme, Icon }) {
  const others = getOtherSubdomains(config.slug);
  const paths = useSubdomainPaths(config.slug);
  const year = new Date().getFullYear();

  return (
    <footer data-testid="subdomain-footer" className="bg-sky-50 border-t border-sky-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-9 h-9 rounded-xl ${theme.primary} flex items-center justify-center`}>
                <Icon size={20} weight="fill" className="text-white" />
              </div>
              <span className="text-lg font-bold text-zinc-900">{config.name}</span>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed">{config.footerTagline}</p>
          </div>

          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Platforms</div>
            <ul className="space-y-2">
              {others.map((item) => (
                <li key={item.slug}>
                  <a
                    href={`https://${item.host}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="https://smartcampus.bizlem.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  Smart Campus
                </a>
              </li>
              <li>
                <a
                  href="https://bizlem.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  Bizlem.com
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Get in Touch</div>
            <p className="text-sm text-zinc-600 mb-4">Let&apos;s design your intelligent infrastructure ecosystem.</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={paths.contact}
                className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white ${theme.primary} ${theme.primaryHover} transition-colors`}
              >
                Request Demo
              </Link>
              <Link
                to={paths.contact}
                className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-zinc-800 border border-zinc-200 hover:border-zinc-400 transition-colors"
              >
                Book Consultation
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-200 text-center text-sm text-zinc-500">
          © {year} {config.name}. All Rights Reserved. · Powered by{" "}
          <a href="https://bizlem.com/" className="hover:text-zinc-800 transition-colors">
            Bizlem
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function SubdomainLayout({ config, children, testId }) {
  const { theme, icon: Icon } = config;

  return (
    <div data-testid={testId} className="min-h-screen bg-sky-50 font-sans">
      <SubdomainHeader config={config} Icon={Icon} theme={theme} />
      {children}
      <SubdomainFooter config={config} theme={theme} Icon={Icon} />
    </div>
  );
}
